import React, { useMemo, useState } from 'react';
import { Box, ButtonBase, Divider, Stack, Tooltip, Typography } from '@mui/material';
import { formatDuration } from '../utils/datetime';

export interface BarSeries {
  key: string;
  label: string;
  color: string;
  total: number; // seconds across the whole range (for the legend)
}

export interface BarBucket {
  key: string;
  label: string; // short x-axis label
  fullLabel: string; // verbose label for the tooltip
  values: Record<string, number>; // seriesKey -> seconds
  total: number; // seconds (all series)
}

interface TimeBarChartProps {
  buckets: BarBucket[];
  series: BarSeries[]; // ordered; first sits at the bottom of each stack
  height?: number;
}

// Candidate gridline steps in seconds — the smallest one that yields ≤ 5
// gridlines is chosen, so axis labels are always round durations.
const STEP_SECONDS = [
  5 * 60, 10 * 60, 15 * 60, 30 * 60,
  3600, 2 * 3600, 3 * 3600, 6 * 3600, 12 * 3600,
  86400, 2 * 86400, 5 * 86400, 7 * 86400, 14 * 86400, 30 * 86400,
];

function computeScale(maxSeconds: number): { top: number; step: number } {
  if (maxSeconds <= 0) return { top: 3600, step: 1800 };
  for (let i = 0; i < STEP_SECONDS.length; i += 1) {
    const step = STEP_SECONDS[i];
    const lines = Math.ceil(maxSeconds / step);
    if (lines <= 5) return { top: step * lines, step };
  }
  const step = Math.ceil(maxSeconds / 5);
  return { top: step * 5, step };
}

const TimeBarChart: React.FC<TimeBarChartProps> = ({ buckets, series, height = 280 }) => {
  const [hidden, setHidden] = useState<Record<string, boolean>>({});
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);

  const visibleSeries = useMemo(() => series.filter((s) => !hidden[s.key]), [series, hidden]);

  // Y-axis scale recomputes from only the visible series, so toggling the
  // legend rescales the chart.
  const { top, step } = useMemo(() => {
    let max = 0;
    for (let i = 0; i < buckets.length; i += 1) {
      let sum = 0;
      for (let j = 0; j < visibleSeries.length; j += 1) {
        sum += buckets[i].values[visibleSeries[j].key] || 0;
      }
      if (sum > max) max = sum;
    }
    return computeScale(max);
  }, [buckets, visibleSeries]);

  const gridValues = useMemo(() => {
    const lines: number[] = [];
    for (let v = 0; v <= top + 1; v += step) lines.push(v);
    return lines;
  }, [top, step]);

  const toggle = (key: string) => setHidden((h) => ({ ...h, [key]: !h[key] }));

  // Thin out x labels when there are many buckets.
  const labelEvery = Math.max(1, Math.ceil(buckets.length / 14));

  return (
    <Box>
      <Box sx={{ display: 'flex' }}>
        {/* Y axis */}
        <Box
          sx={{
            width: 52,
            height,
            position: 'relative',
            flexShrink: 0,
            pr: 1,
          }}
        >
          {gridValues.map((v) => (
            <Typography
              key={v}
              variant="caption"
              sx={{
                position: 'absolute',
                right: 8,
                bottom: `${(v / top) * 100}%`,
                transform: 'translateY(50%)',
                color: 'text.secondary',
                fontVariantNumeric: 'tabular-nums',
                lineHeight: 1,
              }}
            >
              {v === 0 ? '0' : formatDuration(v)}
            </Typography>
          ))}
        </Box>

        {/* Plot area */}
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Box
            sx={{
              position: 'relative',
              height,
              borderLeft: '1px solid',
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            {/* Gridlines */}
            {gridValues.map((v) => (
              <Box
                key={v}
                sx={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  bottom: `${(v / top) * 100}%`,
                  borderTop: '1px dashed',
                  borderColor: v === 0 ? 'transparent' : 'divider',
                  opacity: 0.7,
                }}
              />
            ))}

            {/* Bars */}
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'flex-end',
                gap: buckets.length > 40 ? '1px' : 0.75,
                px: 0.5,
              }}
            >
              {buckets.map((bucket) => {
                const visibleTotal = visibleSeries.reduce(
                  (sum, s) => sum + (bucket.values[s.key] || 0),
                  0
                );
                const dim = hoveredKey !== null && hoveredKey !== bucket.key;
                return (
                  <Tooltip
                    key={bucket.key}
                    arrow
                    placement="top"
                    title={
                      <Box sx={{ py: 0.5, minWidth: 150 }}>
                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                          {bucket.fullLabel}
                        </Typography>
                        {visibleSeries
                          .map((s) => ({ s, secs: bucket.values[s.key] || 0 }))
                          .filter((x) => x.secs > 0)
                          .sort((a, b) => b.secs - a.secs)
                          .map(({ s, secs }) => (
                            <Stack
                              key={s.key}
                              direction="row"
                              alignItems="center"
                              spacing={1}
                              sx={{ mb: 0.25 }}
                            >
                              <Box
                                sx={{
                                  width: 10,
                                  height: 10,
                                  borderRadius: '2px',
                                  bgcolor: s.color,
                                  flexShrink: 0,
                                }}
                              />
                              <Typography variant="caption" sx={{ flexGrow: 1, mr: 1 }}>
                                {s.label}
                              </Typography>
                              <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                {formatDuration(secs)}
                              </Typography>
                            </Stack>
                          ))}
                        {visibleTotal === 0 ? (
                          <Typography variant="caption" color="inherit">
                            No time logged
                          </Typography>
                        ) : (
                          <>
                            <Divider sx={{ my: 0.5, borderColor: 'rgba(255,255,255,0.2)' }} />
                            <Stack direction="row" justifyContent="space-between">
                              <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                Total
                              </Typography>
                              <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                {formatDuration(visibleTotal)}
                              </Typography>
                            </Stack>
                          </>
                        )}
                      </Box>
                    }
                  >
                    <Box
                      onMouseEnter={() => setHoveredKey(bucket.key)}
                      onMouseLeave={() => setHoveredKey(null)}
                      sx={{
                        flex: 1,
                        minWidth: 4,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column-reverse',
                        cursor: 'default',
                        opacity: dim ? 0.5 : 1,
                        transition: 'opacity 120ms ease',
                      }}
                    >
                      {visibleSeries.map((s) => {
                        const secs = bucket.values[s.key] || 0;
                        if (secs <= 0) return null;
                        return (
                          <Box
                            key={s.key}
                            sx={{
                              height: `${(secs / top) * 100}%`,
                              bgcolor: s.color,
                              borderTop: '1px solid rgba(255,255,255,0.85)',
                              '&:first-of-type': { borderTop: 'none' },
                            }}
                          />
                        );
                      })}
                    </Box>
                  </Tooltip>
                );
              })}
            </Box>
          </Box>

          {/* X axis labels */}
          <Box sx={{ display: 'flex', gap: buckets.length > 40 ? '1px' : 0.75, px: 0.5, mt: 0.75 }}>
            {buckets.map((bucket, idx) => (
              <Box key={bucket.key} sx={{ flex: 1, minWidth: 4, textAlign: 'center', overflow: 'hidden' }}>
                {idx % labelEvery === 0 && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      whiteSpace: 'nowrap',
                      fontSize: 11,
                      display: 'block',
                    }}
                  >
                    {bucket.label}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Legend */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          mt: 2.5,
          pl: { xs: 0, sm: '52px' },
        }}
      >
        {series.map((s) => {
          const off = !!hidden[s.key];
          return (
            <ButtonBase
              key={s.key}
              onClick={() => toggle(s.key)}
              focusRipple
              sx={{
                px: 1,
                py: 0.5,
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                opacity: off ? 0.5 : 1,
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '2px',
                  bgcolor: s.color,
                  mr: 0.75,
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, textDecoration: off ? 'line-through' : 'none', mr: 0.75 }}
              >
                {s.label}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {formatDuration(s.total)}
              </Typography>
            </ButtonBase>
          );
        })}
      </Box>
    </Box>
  );
};

export default TimeBarChart;
